/**
 * PPD (Plant Project Development) API
 * Revenue Calculator - Profitability Analysis
 */

import { apiClient } from "./client";

// ============================================================================
// TYPES
// ============================================================================

/** Supported renewable energy plant types */
export type PlantType = "pv" | "wind" | "battery" | "hydro" | "pumped";

/** A named cost entry with a positive amount */
export interface CostItem {
  name: string;
  amount: number;
}

/** Plant type info from /plant-types endpoint */
export interface PlantTypeInfo {
  value: PlantType;
  label: string;
  degradation_rate: number;
}

/** Response from GET /plant-types */
export interface PlantTypesResponse {
  plant_types: PlantTypeInfo[];
}

/** Input parameters for a PPD profitability analysis */
export interface PPDInput {
  // Plant Information
  plant_type: PlantType;
  installed_power_kwp: number;
  operating_hours_per_year: number;
  sale_price_per_kwh: number;
  subsidy_per_kwh?: number;
  price_escalation_rate?: number;
  operating_years: number;

  // Investment Costs
  cost_of_location: number;
  cost_of_plant: number;
  cost_of_grid_connection: number;
  cost_of_substation?: number;
  additional_investment_costs?: CostItem[];
  loan_term_years: number;
  depreciation_years?: number; // Asset useful life, defaults to 25
  annual_interest_rate: number;
  equity_percentage?: number;

  // Operating Costs
  maintenance_cost: number;
  insurance_cost: number;
  taxes: number;
  administration_cost: number;
  inflation_rate?: number;
  additional_operating_costs?: CostItem[];

  // PDF Export Metadata (optional)
  project_name?: string;
  project_address?: string;
  company_name?: string;
  company_address?: string;
  company_contact?: string;
  department?: string;
  developer_name?: string;
  developer_contact?: string;
}

/** One row of the annual revenue/profitability table */
export interface RevenueTableRow {
  year: number;
  performance_factor: number;
  price_per_kwh: number;
  revenue: number;
  operating_costs: number;
  depreciation: number;
  interest: number;
  profit: number;
  cash_flow: number;
  cumulative_cash_flow: number;
  cumulative_profit: number;
}

/** One row of the loan amortization schedule */
export interface AmortizationTableRow {
  year: number;
  interest_payment: number;
  principal_payment: number;
  total_payment: number;
  outstanding_balance: number;
  outstanding_percentage: number;
}

/** Aggregated summary metrics across all operating years */
export interface SummaryMetrics {
  total_investment: number;
  equity_amount: number;
  loan_amount: number;
  annual_production_kwh: number;
  breakeven_year: number | null;
  equity_amortisation_year: number | null;
  investment_amortisation_year: number | null;
  total_revenue: number;
  total_operating_costs: number;
  total_interest: number;
  total_profit: number;
  total_principal: number;
  total_loan_payments: number;
}

/** Pre-structured data for frontend chart rendering */
export interface ChartData {
  investment_breakdown: Record<string, number>;
  operating_costs_breakdown: Record<string, number>;
  yearly_cumulative_cashflow: Array<{ year: number; cumulative_cash_flow: number }>;
  cashflow_breakeven_year: number | null;
}

/** Echo of computed input values for display */
export interface PPDInputEcho {
  total_investment: number;
  equity_amount: number;
  loan_amount: number;
  annual_production_kwh: number;
  effective_base_price: number;
  base_operating_costs: number;
  degradation_rate: number;
  plant_type_label: string;
}

/** One row of the historical-price backtest table (issue #101) */
export interface BacktestTableRow {
  project_year: number;
  historical_year: number;
  /** True for the current-year YTD row (KKEM2's follow-up). */
  is_partial?: boolean;
  performance_factor: number;
  price_per_kwh: number;
  revenue: number;
  operating_costs: number;
  depreciation: number;
  interest: number;
  profit: number;
  cash_flow: number;
  cumulative_cash_flow: number;
  cumulative_profit: number;
}

/** Aggregated totals across the backtest period (issue #101) */
export interface BacktestSummary {
  years_available: number;
  historical_year_start: number | null;
  historical_year_end: number | null;
  /** Explicit list of years actually used — render this when the span has holes. */
  historical_years: number[];
  /** Year of the YTD row, when present (KKEM2's follow-up). Optional so
   *  stale-backend deploys (no field at all) don't break the new frontend. */
  current_year_partial?: number | null;
  /** ISO date (Rome local) when the historical data was last read. */
  data_as_of: string | null;
  total_revenue: number;
  total_operating_costs: number;
  total_profit: number;
  avg_annual_revenue: number;
}

/** Complete result of a PPD profitability analysis */
export interface PPDResult {
  inputs_echo: PPDInputEcho;
  revenue_table: RevenueTableRow[];
  amortization_table: AmortizationTableRow[];
  summary: SummaryMetrics;
  chart_data: ChartData;
  warnings: string[];
  // Issue #101: optional in the type so a stale-frontend-vs-new-backend
  // (or vice versa) deploy can't crash on absent fields. calculatePPD()
  // normalises to [] / null below so downstream code treats them as
  // definitely-defined.
  backtest_table?: BacktestTableRow[];
  backtest_summary?: BacktestSummary | null;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get list of supported plant types with degradation rates
 */
export async function getPlantTypes(): Promise<PlantTypesResponse> {
  return apiClient.get<PlantTypesResponse>("/api/v1/ppd/plant-types");
}

/**
 * Run full PPD profitability analysis
 *
 * Normalises the response so `backtest_table` / `backtest_summary` are always
 * defined — protects against deploy-ordering skew between a stale frontend
 * and a new backend (or vice versa).
 */
export async function calculatePPD(inputs: PPDInput): Promise<PPDResult> {
  const result = await apiClient.post<PPDResult>("/api/v1/ppd/calculate", inputs);
  const normalisedSummary = result.backtest_summary
    ? {
        ...result.backtest_summary,
        // Normalise the nested optional field so UI code can treat it as
        // definitely-defined (KKEM2-follow-up Codex r1 #1).
        current_year_partial: result.backtest_summary.current_year_partial ?? null,
      }
    : null;
  return {
    ...result,
    backtest_table: result.backtest_table ?? [],
    backtest_summary: normalisedSummary,
  };
}

/**
 * Download PPD analysis as PDF
 * Handles blob download and triggers browser save dialog
 */
export async function exportPDF(inputs: PPDInput): Promise<void> {
  const token = localStorage.getItem("vpp_auth_token");
  const baseUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001";

  const response = await fetch(`${baseUrl}/api/v1/ppd/export/pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(inputs),
  });

  if (!response.ok) {
    let errorDetail = "PDF export failed";
    try {
      const errorData = await response.json();
      errorDetail = errorData.detail || errorDetail;
    } catch {
      // Ignore JSON parse error
    }
    throw new Error(errorDetail);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;

  // Build filename from project name
  const projectName = inputs.project_name || "ppd-analysis";
  const safeName = projectName.replace(/[^a-zA-Z0-9-_]/g, "_");
  a.download = `${safeName}-report.pdf`;

  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Format currency value for display
 */
export function formatCurrency(value: number, locale = "de-DE"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format percentage for display (e.g., 0.045 -> "4.5%")
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format energy value for display
 */
export function formatEnergy(kwh: number): string {
  if (kwh >= 1_000_000) {
    return `${(kwh / 1_000_000).toFixed(2)} GWh`;
  }
  if (kwh >= 1_000) {
    return `${(kwh / 1_000).toFixed(0)} MWh`;
  }
  return `${kwh.toFixed(0)} kWh`;
}

/**
 * Get plant type label
 */
export const PLANT_TYPE_LABELS: Record<PlantType, string> = {
  pv: "Photovoltaic (PV)",
  wind: "Wind Farm",
  battery: "Battery Storage",
  hydro: "Hydropower",
  pumped: "Pumped Storage",
};

/**
 * Get plant type degradation rate
 */
export const DEGRADATION_RATES: Record<PlantType, number> = {
  pv: 0.01,
  wind: 0.005,
  battery: 0.02,
  hydro: 0.001,
  pumped: 0.001,
};
